<template>
  <v-row no-gutters align="center">
    <v-col cols="12">
      <v-row no-gutters align="center">
        <v-col cols="auto" class="pr-2 text-left font-weight-medium">
          {{ $i18n.t('admin.dataTree.dataTrees') }}
        </v-col>
        <v-col cols="auto" class="pr-2 text-left">
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click="dialogAction('add')"
            :tooltip="$i18n.t('admin.dataTree.addDataTree')"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-7" align="center">
        <v-col cols="12">
          <ribbon-list
            :listItems="dataTreesArray"
            item-title-key="name"
            item-subtitle-key="dataSourceVerboseName"
            no-items-translation-path="admin.dataTree.noDataTrees"
            style-type="rawHigher"
            :toggleable="false"
          >
            <template #itemButtons-replace="{ item }">
              <dots-menu :items="dotsItems" v-on="getDataTreeDotsListeners(item)" />
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template #datasource.input="data">
        <v-col cols="6">
          <data-input
            v-bind="data"
            v-model="mainInputDialogInputs.find(input => input.name === data.name).value"
            @input="setCurrentDataSourceAttributes"
          />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';

export default {
  name: 'DataTreeSettingsList',
  components: {
    DataInput,
    DotsMenu,
    RibbonList,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  computed: {
    dataSources: get('layers/metadata'),
    dataSourcesNamesArray() {
      return this.$_objectToArray(this.dataSources || [])
        .filter(dataSource => dataSource.scope === 'core')
        .map(dataSource => {
          return { value: dataSource.name, text: dataSource.verbose_name };
        });
    },

    dataTrees: get('dataTree/dataTrees'),
    dataTreesArray() {
      return this.$_objectToArray(this.dataTrees).map(dataTree => {
        return {
          ...dataTree,
          dataSourceVerboseName: this.dataSources[dataTree.datasource]?.verbose_name || '',
        };
      });
    },

    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },

    currentDataSourceTextAttributes() {
      return this.currentDataSourceAttributes?.filter(attribute => attribute.dataType.name === 'text') || [];
    },

    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    dialogsActionsData() {
      return {
        add: {
          agreePath: 'dialog.agreeAdd',
          component: 'MainInputDialog',
        },
        delete: {
          agreePath: 'dialog.agreeDelete',
          component: 'MainDialog',
        },
        edit: {
          agreePath: 'dialog.agreeSave',
          component: 'MainInputDialog',
          initAction: this.setCurrentDataSource,
        },
      };
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.dataTree.actions.${this.currentAction}DataTreeTitle`),
              body: this.$i18n.t(`admin.dataTree.actions.confirmDataTree${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.name,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.dataTree.actions.${this.currentAction}DataTreeTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'name',
          translationPath: 'admin.dataTree.attributes.name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'datasource',
          translationPath: 'admin.dataTree.attributes.datasource',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesNamesArray,
        },
        {
          name: 'agg_attribute_1',
          translationPath: 'admin.dataTree.attributes.attribute1',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes,
          disabled: !this.currentDataSourceAttributes,
        },
        {
          name: 'agg_attribute_2',
          translationPath: 'admin.dataTree.attributes.attribute2',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes,
          disabled: !this.currentDataSourceAttributes,
        },
        {
          name: 'agg_attribute_3',
          translationPath: 'admin.dataTree.attributes.attribute3',
          subtitle: this.$i18n.t('default.optional'),
          cols: '12',
          clearable: true,
          dataType: { name: 'select' },
          items: this.currentDataSourceAttributes,
          disabled: !this.currentDataSourceAttributes,
        },
        {
          name: 'type_attribute',
          translationPath: 'admin.dataTree.attributes.type',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: { name: 'select' },
          items: this.currentDataSourceTextAttributes,
          disabled: !this.currentDataSourceAttributes,
        },
      ];
      const mainInputDialogInputsDict = {
        add: elementEditInputs,
        edit: elementEditInputs.map(input => {
          if (input.name === 'datasource') {
            return { ...input, disabled: true };
          }
          return input;
        }),
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return {
          ...input,
          value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || null)),
        };
      });
    },
  },
  data: () => ({
    currentAction: '',
    currentActionData: undefined,

    currentDataSource: '',
    currentDataSourceAttributes: undefined,

    dotsItems: [{ name: 'edit' }, { name: 'delete' }],

    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
  }),
  methods: {
    ...call('dataTree/*DataTree'),

    getDataTreeDotsListeners(item) {
      return this.dotsItems.reduce((total, action) => {
        total[action.name] = () => {
          this.dialogAction(action.name, item);
        };
        return total;
      }, {});
    },

    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) initAction(value);
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler(data) {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}DataTree`](data);
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    async handlerDeleteDataTree() {
      await this.deleteDataTree(this.currentActionData.id);
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerAddDataTree() {
      await this.addDataTree(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'));
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    async handlerEditDataTree() {
      await this.editDataTree({
        id: this.currentActionData.id,
        payload: this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'),
      });
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },

    setCurrentDataSource(value) {
      this.setCurrentDataSourceAttributes(value['datasource']);
    },
    setCurrentDataSourceAttributes(value) {
      const currentValues = JSON.parse(
        JSON.stringify(this.$_arrayToObject(this.mainInputDialogInputs, 'name', 'value'))
      );
      this.currentDataSourceAttributes = (this.dataSources[value]?.attributes_schema?.attributes || [])
        .filter(attribute => attribute.data_type?.name !== 'geometric')
        .map(attribute => {
          return {
            value: attribute.name,
            text: attribute.verbose_name,
            dataType: attribute.data_type,
          };
        });
      setTimeout(() => {
        Object.keys(currentValues).forEach(key => {
          this.mainInputDialogInputs.find(input => input.name === key).value = currentValues[key];
        });
      });
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.currentActionData = undefined;
          this.currentDataSourceAttributes = undefined;
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
  },
};
</script>
